import { SkihirePoiInterface } from '../interfaces/skihire-poi-interface';

export enum codetype {
  promo = 'promotioncode',
  coupon = 'couponcode'
}

export interface BookingParametersInterface {
  slug: string; // townslug
  start?: Date; // startdate
  end?: Date; // enddate
  ages?: Array<number>; // the age in numbers of all persons
  serviceId?: number; // the service id
  partner?: string;
  poi?: SkihirePoiInterface;
  userDefinedSearch?: boolean; // true if user hit search once
  codes?: Array<ActioncodeInterface>; // all (valid) codes a user entered. currently only one promo and one couponcode is allowed
  cid?: string; // the cart id if the user was already on the product selection
  shopId?: number; // the shop-id of the last shop the user used in the product-selection
  windowname?: string; // the session window name so that browsers won't copy the session params
}

export interface ActioncodeInterface {
  type: codetype;
  text: string;
  euroBalanceRaw?: number; // balance in euro
  rates?: { [key: string]: number }; // exchange rates to update displayed info
  errorCode?: number; // 404100
  errorType?: string; // CouponDoesNotExist
  validFrom?: Date;
  validTo?: Date;
}

export class BookingParameters implements BookingParametersInterface {
  constructor(
    public slug: string,
    public start?: Date,
    public end?: Date,
    public ages?: Array<number>,
    public serviceId?: number,
    public partner?: string,
    public poi?: SkihirePoiInterface,
    public userDefinedSearch?: boolean,
    public codes?: Array<ActioncodeInterface>,
    public cid?: string,
    public shopId?: number,
    public windowname?: string
  ) {
    if (!codes) {
      this.codes = [];
    } else {
      this.codes.forEach(code => {
        code.validFrom = code.validFrom ? new Date(code.validFrom) : null;
        code.validTo = code.validTo ? new Date(code.validTo) : null;
      });
    }
  }

  update(bps: BookingParametersInterface) {
    bps.start.setHours(0, 0, 0);
    bps.end.setHours(0, 0, 0);
    this.slug = bps.slug;
    this.start = bps.start;
    this.end = bps.end;
    this.ages = bps.ages;
    this.serviceId = bps.serviceId;
    this.partner = bps.partner;
    this.poi = bps.poi;
    this.userDefinedSearch = bps.userDefinedSearch;
    this.codes = bps.codes;
    this.cid = bps.cid;
    this.shopId = bps.shopId;
    this.windowname = bps.windowname;
  }

  copy(): BookingParameters {
    return new BookingParameters(
      this.slug,
      this.start,
      this.end,
      this.ages,
      this.serviceId,
      this.partner,
      this.poi,
      this.userDefinedSearch,
      this.codes,
      this.cid,
      this.shopId,
      this.windowname
    );
  }

  getDurationInDays(): number {
    // calculate number of days between start and enddate
    let days = Math.round(Math.abs((this.start.getTime() - this.end.getTime()) / (24 * 60 * 60 * 1000)));
    days += 1;
    if (days === 0) {
      days = 1;
    }

    return days;
  }

  /**
   * sets or ads the given code. only one codeType is allowed at once.
   * for example: one promocode and one couponcode
   * @param code
   */
  setActioncode(code: ActioncodeInterface) {
    if (code.text.length == 0) {
      this.removeActioncode(code);
    } else {
      const match = this.codes.find(_code => _code.type === code.type);
      if (code && match) {
        match.text = code.text;
        match.euroBalanceRaw = code.euroBalanceRaw;
        match.rates = code.rates;
        match.errorCode = code.errorCode;
        match.errorType = code.errorType;
        match.validFrom = code.validFrom;
        match.validTo = code.validTo;
      } else {
        this.codes.push(code);
      }
    }
  }

  /**
   * returns the first promocode in codes or undefined if none exists (currently only one promocode is allowed)
   */
  getPromocode(): ActioncodeInterface | undefined {
    return this.codes.find(_code => _code.type === codetype.promo);
  }

  /**
   * returns the first couponcode in codes or undefined if none exists (currently only one couponcode is allowed)
   */
  getCouponcode(): ActioncodeInterface | undefined {
    return this.codes.find(_code => _code.type === codetype.coupon);
  }

  removeActioncode(code: ActioncodeInterface) {
    this.codes = this.codes.filter(_code => {
      if (code.text) {
        return _code.type !== code.type || _code.text !== code.text;
      } else {
        return _code.type !== code.type;
      }
    });
  }

  getNumberOfAdults(): number {
    return this.ages.filter(value => value >= 18).length;
  }

  getNumberOfChildren(): number {
    return this.ages.filter(value => value <= 17).length;
  }
}

export function copyBookingParameters(bookingParameters: BookingParametersInterface): BookingParameters {
  return new BookingParameters(
    bookingParameters.slug,
    bookingParameters.start,
    bookingParameters.end,
    bookingParameters.ages,
    bookingParameters.serviceId,
    bookingParameters.partner,
    bookingParameters.poi,
    bookingParameters.userDefinedSearch,
    bookingParameters.codes,
    bookingParameters.cid,
    bookingParameters.shopId,
    bookingParameters.windowname
  );
}
